// InvoiceCoverageTab (2026-05-13)
// 請求書処理 page の 'sub tab' (= 受信一覧)。 ARICA 受信全件を機械的に分類して
// 状態 (対応待ち(取込) / 処理中 / 対応待ち(要修正) / 完了(処理済) / 除外(却下) / 口座引落) を可視化する。
//
// ABSOLUTE RULE: 金額 field は API から受信しても UI に絶対 render しない。
// (関連 keyword は本 file に一切記述しない方針)

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  CardBase,
  HStack,
  VStack,
  Button,
  SearchField,
  SelectBox,
  Note,
  Text,
  ProgressBar,
  MessageBlock,
} from '@freee_jp/vibes';
import {
  MdRefresh,
  MdOpenInNew,
  MdCheckCircle,
  MdWarning,
  MdEdit,
  MdBlock,
  MdRestore,
  MdSwapHoriz,
  MdLabel,
  MdPerson,
  MdSettings,
  MdPlayArrow,
  MdSend,
  MdAccountBalance,
  MdClose,
  MdAdd,
  MdDelete,
  MdStop,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdIndeterminateCheckBox,
  MdClear,
} from 'react-icons/md';
import { PdfViewer } from './PdfViewer';
import { coverageUiStatusToUnified } from '../lib/statusVocab';
import {
  fetchPaymentWindows,
  createPaymentWindow,
  updatePaymentWindow,
  deletePaymentWindow,
  bulkOverrideByWindow,
  bulkOverrideByRowids,
  startBulkDispatch,
  bulkDispatchV5,
  getBulkDispatchStatus,
  abortBulkDispatch,
  resumeBulkDispatch,
  type PaymentWindow,
  type PaymentWindowOverlapConflict,
  type BulkOverrideByWindowResponse,
  type BulkDispatchBatchStatus,
  notifyAuditBlocked,
} from '../api/client';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CoverageRowOverride {
  skip: boolean;
  flow_type: 'bank_auto_debit' | '25th_payment' | 'other' | null;
  doc_type: 'invoice' | 'receipt' | 'quotation' | 'order' | 'other' | null;
  partner_id: number | null;
  partner_name: string | null;
  reason: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface CoverageRow {
  rowid: number;
  attachment_rowid?: number | null;
  partner_id: number;
  partner_name: string | null;
  receive_date: string;
  issue_date: string | null;
  due_date: string | null;
  // (金額系 field は受信するが本 component で参照しないので型から除外)
  document_type: string | null;
  document_format?: string | null;
  payment_method: string | null;
  invoice_number: string | null;
  current_status: string;          // registered|proposed|approved|rejected|untouched|skip_*
  d1_proposal_id: number | null;
  freee_deal_id: number | null;
  ref_number: string | null;
  rejection_reason?: string | null;
  payment_target_estimate: string;
  payment_target_source?: string | null;
  matched_window_id?: number | null;
  bulk_action_eligibility?: {
    eligible_for_bulk_override: boolean;
    eligible_for_bulk_dispatch: boolean;
    is_arica_direct_debit_candidate: boolean;
  };
  pdf_url: string;
  attachment_filename?: string | null;
  approved_at?: string | null;
  // ARICA 推測値 (= 参考表示用、 信用しない)
  arica_document_type?: string | null;
  arica_document_format?: string | null;
  arica_payment_method?: string | null;
  arica_partner_id?: number | null;
  arica_partner_name?: string | null;
  // user 確定値 override (D1 永続)
  override?: CoverageRowOverride | null;
}

export interface CoverageResponse {
  query: { from: string; to: string | null; max_pages: number };
  arica_scan: {
    fetched_pages: number;
    empty_stop_at: number | null;
    total_unique: number;
    after_filter: number;
    hint: string;
  };
  summary: {
    receive_window: { from: string; to: string };
    total_received: number;
    by_status: Record<string, number>;
    untouched_total: number;
    untouched_5_25_due_estimate: number;
    bulk_action_counts?: {
      eligible_for_bulk_override: number;
      eligible_for_bulk_dispatch: number;
      arica_direct_debit_candidates: number;
      active_window: { id: number; label: string; target_payment_month: string } | null;
    };
    // (金額系 集計 field は受信しても本 component で参照しないので型から除外)
  };
  rows: CoverageRow[];
}

export type UiStatus = 'unprocessed' | 'in_progress' | 'needs_fix' | 'done' | 'rejected' | 'auto_debit';

// 語・色は統一語彙 (lib/statusVocab.ts coverageUiStatusToUnified)。
//   unprocessed = 取込を開始すれば前進する「あなたの番」= 橙 (赤をやめる — 赤は失敗事実のみ)。
//   auto_debit はワークフロー状態ではなくフロー種別 → ローカル定義のまま。
const uiStatusBadge = (s: Exclude<UiStatus, 'auto_debit'>) => {
  const u = coverageUiStatusToUnified(s);
  return { label: u.label, bg: u.bg, color: u.fg, border: u.bd };
};
const UI_STATUS_CONFIG: Record<UiStatus, { label: string; bg: string; color: string; border: string }> = {
  unprocessed: uiStatusBadge('unprocessed'),
  in_progress: uiStatusBadge('in_progress'),
  needs_fix:   uiStatusBadge('needs_fix'),
  done:        uiStatusBadge('done'),
  rejected:    uiStatusBadge('rejected'),
  auto_debit:  { label: '口座引落', bg: '#e8f0fe', color: '#2563c7', border: '#a3c0f5' },
};

const UI_STATUS_ORDER: UiStatus[] = ['unprocessed', 'in_progress', 'needs_fix', 'done', 'rejected', 'auto_debit'];

// ---------------------------------------------------------------------------
// classifyRow
// ---------------------------------------------------------------------------

export function classifyRow(r: CoverageRow): UiStatus {
  // override 優先 (= user 確定値、 ARICA バイアス排除)
  if (r.override?.skip) return 'rejected';
  if (r.current_status.startsWith('skip_')) return 'rejected';
  if (r.override?.flow_type === 'bank_auto_debit') return 'auto_debit';
  if (r.current_status === 'registered' && r.freee_deal_id) return 'done';
  if (r.current_status === 'rejected') return 'rejected';
  if (r.current_status === 'proposed') {
    const needsFix =
      !r.partner_name || r.partner_name === 'unknown' ||
      !r.issue_date || !r.invoice_number;
    return needsFix ? 'needs_fix' : 'in_progress';
  }
  if (r.current_status === 'approved') return 'in_progress';
  return 'unprocessed';
}

// ---------------------------------------------------------------------------
// session_id helper (= localStorage)
// ---------------------------------------------------------------------------

function getSessionId(): string | null {
  try {
    return localStorage.getItem('claude_session_id') || null;
  } catch {
    return null;
  }
}

// 2026-05-18 fix: UI mount 時に session を自動 open + heartbeat。
// browser user は明示的に session/open する手段がないので auto-bootstrap で UX 補正。
async function ensureSession(): Promise<string | null> {
  try {
    const existing = localStorage.getItem('claude_session_id');
    if (existing) {
      // heartbeat で生存確認 (= server-side で closed なら新規 open)
      const hb = await fetch(`${API_BASE}/api/claude/session/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: existing }),
      }).catch(() => null);
      if (hb && hb.ok) return existing;
      // heartbeat 失敗 → 新規 open
    }
    const r = await fetch(`${API_BASE}/api/claude/session/open`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actor: 'browser-ui', scope: 'invoice_coverage_ui', lease_minutes: 120 }),
    });
    if (!r.ok) {
      // 2026-07-15 blockfix: audit blocking 409 は共通ダイアログ (自動修復ボタン付き) へ通知
      if (r.status === 409) notifyAuditBlocked(await r.json().catch(() => null));
      return null;
    }
    const data = await r.json();
    if (data.session_id) {
      localStorage.setItem('claude_session_id', data.session_id);
      return data.session_id;
    }
  } catch {}
  return null;
}

function statusBadge(s: UiStatus): React.ReactNode {
  const cfg = UI_STATUS_CONFIG[s];
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 500,
      background: cfg.bg,
      color: cfg.color,
      border: `1px solid ${cfg.border}`,
      whiteSpace: 'nowrap',
    }}>
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 受信期間 default (= 前月 21 日)
// ---------------------------------------------------------------------------

function defaultFromDate(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const fromY = m === 0 ? y - 1 : y;
  const fromM = m === 0 ? 12 : m;
  return `${fromY}-${String(fromM).padStart(2, '0')}-21`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InvoiceCoverageTab: React.FC = () => {
  const [resp, setResp] = useState<CoverageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>(defaultFromDate());
  const [toDate, setToDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<UiStatus | 'all'>('all');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [includeSkipped, setIncludeSkipped] = useState<boolean>(false);
  const [activeRow, setActiveRow] = useState<CoverageRow | null>(null);
  const [windowModalOpen, setWindowModalOpen] = useState(false);
  const [bulkOverrideOpen, setBulkOverrideOpen] = useState(false);
  const [bulkDispatchOpen, setBulkDispatchOpen] = useState(false);
  const [aricaReviewOpen, setAricaReviewOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState<boolean>(() => !!getSessionId());
  // 選択ベース一括処理 (= filter / sort 関係なく rowid を保持)
  const [selectedRowids, setSelectedRowids] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [bulkErr, setBulkErr] = useState<string | null>(null);
  const [bulkToast, setBulkToast] = useState<string | null>(null);
  const [selectionBatchId, setSelectionBatchId] = useState<string | null>(null);

  // 2026-05-18: mount 時に session auto-bootstrap (= browser user 用)
  useEffect(() => {
    if (sessionReady) return;
    ensureSession().then((sid) => {
      if (sid) setSessionReady(true);
    });
  }, [sessionReady]);

  // 2026-06-16: バックグラウンド更新 (silent=true) は「読込中」を出さず、内容が
  //   前回と同一なら setResp しない。= 60s 毎にテーブルが「読込中…」へ崩れて高さが
  //   縮み、 scroll がトップへ戻される問題を解消。 初回ロード/手動「再読み込み」時のみ
  //   loading 表示する。
  const lastSigRef = useRef<string>('');
  const reload = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/api/admin/invoice-coverage-audit`);
      url.searchParams.set('from', fromDate);
      if (toDate) url.searchParams.set('to', toDate);
      const r = await fetch(url.toString());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j: CoverageResponse = await r.json();
      const sig = JSON.stringify(j?.summary ?? null) + '|' +
        (j?.rows || []).map((x: any) => `${x.rowid}:${x.current_status}`).join(',');
      if (sig !== lastSigRef.current) {
        lastSigRef.current = sig;
        setResp(j);
      }
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = setInterval(() => reload(true), 60000);
    return () => clearInterval(t);
  }, [reload]);

  const enriched = useMemo(() => {
    if (!resp) return [];
    return resp.rows
      .filter((r) => includeSkipped || !r.current_status.startsWith('skip_'))
      .map((r) => ({ row: r, ui: classifyRow(r) }));
  }, [resp, includeSkipped]);

  const counts = useMemo(() => {
    const c: Record<UiStatus, number> = {
      unprocessed: 0, in_progress: 0, needs_fix: 0, done: 0, rejected: 0, auto_debit: 0,
    };
    enriched.forEach((e) => { c[e.ui] += 1; });
    return c;
  }, [enriched]);

  const docTypeOptions = useMemo(() => {
    const set = new Set<string>();
    enriched.forEach(({ row }) => {
      const dt = row.override?.doc_type || row.document_type || row.arica_document_type;
      if (dt) set.add(dt);
    });
    const options = [{ name: '全種別', value: 'all' }];
    Array.from(set).sort().forEach((dt) => options.push({ name: dt, value: dt }));
    return options;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return enriched
      .filter(({ row, ui }) => {
        if (statusFilter !== 'all' && ui !== statusFilter) return false;
        if (docTypeFilter !== 'all') {
          const dt = row.override?.doc_type || row.document_type || row.arica_document_type;
          if (dt !== docTypeFilter) return false;
        }
        if (q) {
          const hay = [row.partner_name, row.invoice_number, String(row.rowid), row.ref_number]
            .filter(Boolean).join(' ').toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // receive_date desc, then rowid desc
        const ra = a.row.receive_date || '';
        const rb = b.row.receive_date || '';
        const dr = rb.localeCompare(ra);
        if (dr !== 0) return dr;
        return b.row.rowid - a.row.rowid;
      });
  }, [enriched, statusFilter, docTypeFilter, searchText]);

  const totalForProgress = counts.unprocessed + counts.in_progress + counts.needs_fix + counts.done + counts.rejected + counts.auto_debit;
  const doneForProgress = counts.done + counts.rejected + counts.auto_debit;

  const aricaWarn = resp && resp.arica_scan.empty_stop_at == null;

  // --- 選択 (= filter / sort 関係なく rowid を保持) ---
  const filteredRowids = useMemo(() => filtered.map(({ row }) => row.rowid), [filtered]);
  const filteredRowidSet = useMemo(() => new Set(filteredRowids), [filteredRowids]);
  const selectedFilteredCount = useMemo(() => {
    let n = 0;
    selectedRowids.forEach((id) => { if (filteredRowidSet.has(id)) n++; });
    return n;
  }, [selectedRowids, filteredRowidSet]);
  const allFilteredSelected = filteredRowids.length > 0 && selectedFilteredCount === filteredRowids.length;
  const someFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected;
  const offFilterSelectedCount = selectedRowids.size - selectedFilteredCount;

  const toggleRow = useCallback((rowid: number) => {
    setSelectedRowids((prev) => {
      const next = new Set(prev);
      if (next.has(rowid)) next.delete(rowid); else next.add(rowid);
      return next;
    });
  }, []);

  const toggleAllFiltered = useCallback(() => {
    setSelectedRowids((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        // 全 filtered を解除 (filter 外の選択は維持)
        filteredRowids.forEach((id) => next.delete(id));
      } else {
        // 全 filtered を選択
        filteredRowids.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [filteredRowids, allFilteredSelected]);

  const clearSelection = useCallback(() => {
    setSelectedRowids(new Set());
  }, []);

  // --- 選択 bulk action 群 (= ensureSession 経由) ---
  const runOverride = useCallback(async (
    label: string,
    body: { flow_type?: '25th_payment' | 'bank_auto_debit' | 'other'; skip?: boolean; reason: string },
  ) => {
    if (selectedRowids.size === 0) return;
    if (!window.confirm(`${label} ${selectedRowids.size} 件 を実行しますか?`)) return;
    setBulkBusy(true);
    setBulkErr(null);
    setBulkToast(null);
    try {
      const sid = await ensureSession();
      if (!sid) throw new Error('session_id 取得失敗');
      const rowids = Array.from(selectedRowids);
      const r = await bulkOverrideByRowids({
        rowids,
        ...body,
        dry_run: false,
        session_id: sid,
      });
      setBulkToast(`${label}: ${r.applied?.inserted ?? 0} 件 確定`);
      setSelectedRowids(new Set());
      reload();
    } catch (e: any) {
      setBulkErr(e?.message || String(e));
    } finally {
      setBulkBusy(false);
    }
  }, [selectedRowids, reload]);

  const onBulk25th = useCallback(() => {
    runOverride('25日払い 確定', { flow_type: '25th_payment', reason: 'selection_bulk_25th_payment' });
  }, [runOverride]);
  const onBulkAutoDebit = useCallback(() => {
    runOverride('口座引落 確定', { flow_type: 'bank_auto_debit', reason: 'selection_bulk_bank_auto_debit' });
  }, [runOverride]);
  const onBulkSkip = useCallback(() => {
    runOverride('skip', { skip: true, reason: 'selection_bulk_skip' });
  }, [runOverride]);

  const onBulkDispatchSelected = useCallback(async () => {
    if (selectedRowids.size === 0) return;
    if (!window.confirm(`選択中 ${selectedRowids.size} 件 を dispatch しますか?`)) return;
    setBulkBusy(true);
    setBulkErr(null);
    setBulkToast(null);
    try {
      const sid = await ensureSession();
      if (!sid) throw new Error('session_id 取得失敗');
      const rowids = Array.from(selectedRowids);
      const r = await startBulkDispatch({
        rowids,
        flow_type: '25th_payment',
        concurrency: 3,
        dry_run: false,
        session_id: sid,
      });
      if (!r.batch_id) {
        throw new Error('batch_id 取得失敗');
      }
      setSelectionBatchId(r.batch_id);
      setSelectedRowids(new Set());
    } catch (e: any) {
      setBulkErr(e?.message || String(e));
    } finally {
      setBulkBusy(false);
    }
  }, [selectedRowids]);

  // 2026-05-20 (G-step2): v5 dispatch button (= dual-reader + orchestrator)
  //   v4.2 path は worker 側で stub 化 (= runReadersV42 → runReadersV5 redirect)、
  //   alias endpoint 名のみ保持して内部は完全に v5 に統一。
  const onBulkDispatchV5Selected = useCallback(async () => {
    if (selectedRowids.size === 0) return;
    if (selectedRowids.size > 50) {
      setBulkErr(`v5 bulk dispatch は 50 件以下まで (${selectedRowids.size} 件 選択中)`);
      return;
    }
    if (!window.confirm(`選択中 ${selectedRowids.size} 件 を v5 dispatch しますか? (各 rid で dual-reader 並列実行 + orchestrator verdict + 5 層 cascade 監査)`)) return;
    setBulkBusy(true);
    setBulkErr(null);
    setBulkToast(null);
    try {
      const rowids = Array.from(selectedRowids);
      const r = await bulkDispatchV5(rowids);
      setBulkToast(`v5 dispatch: ${r.dispatched}/${r.total} 件 成功 (= 各 rid dual-reader emit、 CLI subagent が pickup 順次処理 → 完了後 仕訳検証ページに表示)`);
      setSelectedRowids(new Set());
    } catch (e: any) {
      setBulkErr(e?.message || String(e));
    } finally {
      setBulkBusy(false);
    }
  }, [selectedRowids]);

  // toast 自動消去
  useEffect(() => {
    if (!bulkToast) return;
    const t = setTimeout(() => setBulkToast(null), 4000);
    return () => clearTimeout(t);
  }, [bulkToast]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Header card */}
      <CardBase paddingSize="small" mb={0.5}>
        <HStack gap={1} alignItems="center" justifyContent="space-between">
          <HStack gap={0.5} alignItems="center">
            <Text size={1} weight="bold">受信期間</Text>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{
                padding: '4px 6px', border: '1px solid #dbd9d7', borderRadius: 4,
                fontSize: 13, fontFamily: 'inherit',
              }}
            />
            <Text size={0.875} color="burnt">〜</Text>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              placeholder="(指定なし = 今日まで)"
              style={{
                padding: '4px 6px', border: '1px solid #dbd9d7', borderRadius: 4,
                fontSize: 13, fontFamily: 'inherit',
              }}
            />
            <Button appearance="secondary" small onClick={() => reload()} disabled={loading}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdRefresh /> {loading ? '読込中…' : '再読み込み'}
              </span>
            </Button>
          </HStack>
          {resp && (
            <Note>
              {resp.arica_scan.fetched_pages} pages / {resp.arica_scan.after_filter} 件 (期間内){' '}
              {resp.arica_scan.empty_stop_at ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MdCheckCircle style={{ color: '#287a48' }} /> 完全スキャン
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MdWarning style={{ color: '#c33939' }} /> 上限到達 (max_pages)
                </span>
              )}
            </Note>
          )}
        </HStack>
        {aricaWarn && (
          <div style={{ marginTop: 8 }}>
            <MessageBlock error>
              ARICA scan が max_pages 上限に到達しました。 全件 scan されていない可能性があります。
              受信期間を絞るか worker.js の max_pages を増やして再読込してください。
            </MessageBlock>
          </div>
        )}
        {error && (
          <div style={{ marginTop: 8 }}>
            <MessageBlock error>取得失敗: {error}</MessageBlock>
          </div>
        )}
      </CardBase>

      {/* 7-state count cards (responsive auto-fit, height-locked) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 8,
      }}>
        <button
          type="button"
          onClick={() => setStatusFilter('all')}
          aria-pressed={statusFilter === 'all'}
          style={{
            border: statusFilter === 'all' ? '2px solid #2e7cf6' : '1px solid #dbd9d7',
            background: '#fff', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
            textAlign: 'left', minHeight: 64,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            transition: 'transform .08s ease, box-shadow .08s ease',
            boxShadow: statusFilter === 'all' ? '0 0 0 1px #2e7cf6 inset' : 'none',
          }}
        >
          <div style={{ fontSize: 11, color: '#6b6560', marginBottom: 2, fontWeight: 500 }}>全件</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: '#1c1916', lineHeight: 1.1 }}>{totalForProgress}</div>
        </button>
        {UI_STATUS_ORDER.map((s) => {
          const cfg = UI_STATUS_CONFIG[s];
          const active = statusFilter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(active ? 'all' : s)}
              aria-pressed={active}
              style={{
                border: active ? `2px solid ${cfg.color}` : `1px solid ${cfg.border}`,
                background: active ? cfg.bg : '#fff',
                padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                textAlign: 'left', minHeight: 64,
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                transition: 'transform .08s ease, box-shadow .08s ease',
              }}
            >
              <div style={{ fontSize: 11, color: cfg.color, marginBottom: 2, fontWeight: 500 }}>{cfg.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: cfg.color, lineHeight: 1.1 }}>{counts[s]}</div>
            </button>
          );
        })}
      </div>

      {/* Bulk Action Bar (半自動処理機構: window 設定 + bulk override + bulk dispatch) */}
      <BulkActionBar
        activeWindow={resp?.summary?.bulk_action_counts?.active_window || null}
        counts={resp?.summary?.bulk_action_counts || {
          eligible_for_bulk_override: 0,
          eligible_for_bulk_dispatch: 0,
          arica_direct_debit_candidates: 0,
          active_window: null,
        }}
        onOpenWindowConfig={() => setWindowModalOpen(true)}
        onBulkOverride={() => setBulkOverrideOpen(true)}
        onBulkDispatch={() => setBulkDispatchOpen(true)}
        onReviewAricaDirectDebit={() => setAricaReviewOpen(true)}
      />

      {/* Progress (done + rejected + auto_debit / total) */}
      {totalForProgress > 0 && (
        <CardBase paddingSize="small" mb={0.5}>
          <HStack gap={1} alignItems="center">
            <div style={{ flex: 1 }}>
              <ProgressBar
                value={doneForProgress}
                maxValue={totalForProgress}
                width="full"
                ariaLabel="処理進捗"
              />
            </div>
            <Text size={0.875} color="burnt">
              {doneForProgress} / {totalForProgress} ({totalForProgress > 0 ? Math.round((doneForProgress / totalForProgress) * 100) : 0}%)
            </Text>
          </HStack>
        </CardBase>
      )}

      {/* Filter bar */}
      <CardBase paddingSize="small" mb={0.5}>
        <HStack gap={0.5} alignItems="center">
          <SearchField
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="取引先名・請求書番号・rowid で検索"
            width="large"
            small
          />
          <SelectBox
            options={[
              { name: '全状態', value: 'all' },
              ...UI_STATUS_ORDER.map((s) => ({ name: UI_STATUS_CONFIG[s].label, value: s })),
            ]}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UiStatus | 'all')}
            width="small"
            small
            label="状態"
          />
          <SelectBox
            options={docTypeOptions}
            value={docTypeFilter}
            onChange={(e) => setDocTypeFilter(e.target.value)}
            width="small"
            small
            label="種別"
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeSkipped}
              onChange={(e) => setIncludeSkipped(e.target.checked)}
            />
            skip 行も表示
          </label>
          <div style={{ marginLeft: 'auto' }}>
            <Note>{filtered.length} 件</Note>
          </div>
        </HStack>
      </CardBase>

      {/* Table */}
      <CardBase paddingSize="zero" mb={0.5}>
        <style>{`
          .coverage-table tbody tr { transition: background .08s ease; }
          .coverage-table tbody tr:nth-child(even) { background: #fbfaf9; }
          .coverage-table tbody tr:hover { background: #f1f6ff !important; }
        `}</style>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #ece9e6', background: '#fafaf9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <HStack gap={0.5} alignItems="center">
            <Note>並び順: 受信日 (新しい順)</Note>
            {selectedRowids.size > 0 && (
              <>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 600, color: '#2563c7',
                  padding: '2px 8px', background: '#e8f0fe',
                  border: '1px solid #a3c0f5', borderRadius: 12,
                }}>
                  <MdCheckBox style={{ fontSize: 14 }} />
                  {selectedRowids.size} 件選択中
                  {offFilterSelectedCount > 0 && (
                    <span style={{ marginLeft: 4, color: '#8a6a16' }}>
                      (絞り込み外 {offFilterSelectedCount} 件)
                    </span>
                  )}
                </span>
                <Button appearance="secondary" small onClick={clearSelection}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <MdClear /> 選択解除
                  </span>
                </Button>
              </>
            )}
          </HStack>
          <Note>クリックで詳細パネルを開く</Note>
        </div>
        <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
          <table className="coverage-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'auto' }}>
            <thead style={{ background: '#f8f6f4', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ ...cellHead, width: 36, textAlign: 'center' }}>
                  <SelectAllCheckbox
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected}
                    disabled={filteredRowids.length === 0}
                    onChange={toggleAllFiltered}
                  />
                </th>
                <th style={{ ...cellHead, width: 100 }}>受信日</th>
                <th style={{ ...cellHead, minWidth: 220 }}>業者</th>
                <th style={{ ...cellHead, width: 110 }}>種別</th>
                <th style={{ ...cellHead, width: 140 }}>請求書番号</th>
                <th style={{ ...cellHead, width: 100 }}>発生日</th>
                <th style={{ ...cellHead, width: 110 }}>支払期日</th>
                <th style={{ ...cellHead, width: 90 }}>状態</th>
                <th style={{ ...cellHead, width: 80, textAlign: 'right' }}>　</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#6b6560' }}>
                    読込中…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 32, textAlign: 'center', color: '#6b6560' }}>
                    該当行なし
                  </td>
                </tr>
              )}
              {!loading && filtered.map(({ row, ui }) => (
                <tr
                  key={row.rowid}
                  onClick={() => setActiveRow(row)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #ece9e6',
                    background: selectedRowids.has(row.rowid) ? '#e8f0fe' : undefined,
                  }}
                >
                  <td
                    style={{ ...cellBody, textAlign: 'center', width: 36 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRowids.has(row.rowid)}
                      onChange={(e) => { e.stopPropagation(); toggleRow(row.rowid); }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`#${row.rowid} を選択`}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={cellBody}>{row.receive_date || '—'}</td>
                  <td style={cellBody}>
                    <div style={{ fontWeight: 600, color: '#1c1916', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{row.partner_name || <span style={{ color: '#c33939' }}>(未設定)</span>}</span>
                      {row.override && (
                        <span style={{
                          fontSize: 10, padding: '1px 6px', background: '#e8f0fe', color: '#2563c7',
                          border: '1px solid #a3c0f5', borderRadius: 10, fontWeight: 500,
                        }}>Override</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#8a8580', marginTop: 2 }}>#{row.rowid}</div>
                  </td>
                  <td style={cellBody}>
                    {row.override?.doc_type ? (
                      <span style={{
                        fontSize: 11, padding: '1px 6px',
                        background: '#e8f0fe', color: '#2563c7',
                        border: '1px solid #a3c0f5', borderRadius: 3, fontWeight: 500,
                      }}>{row.override.doc_type}</span>
                    ) : row.arica_document_type ? (
                      <span style={{
                        fontSize: 11, padding: '1px 6px',
                        background: '#f5f3f1', color: '#bdb8b3',
                        borderRadius: 3, fontStyle: 'italic',
                      }} title="ARICA 推測 (信用しない)">{row.arica_document_type} ?</span>
                    ) : (
                      <span style={{ color: '#bdb8b3', fontSize: 11 }}>未確認</span>
                    )}
                  </td>
                  <td style={{ ...cellBody, fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace', fontSize: 12 }}>
                    {row.invoice_number || <span style={{ color: '#bdb8b3' }}>—</span>}
                  </td>
                  <td style={cellBody}>{row.issue_date || <span style={{ color: '#bdb8b3' }}>—</span>}</td>
                  <td style={cellBody}>{row.due_date || <span style={{ color: '#bdb8b3' }}>—</span>}</td>
                  <td style={cellBody}>{statusBadge(ui)}</td>
                  <td style={{ ...cellBody, textAlign: 'right' }}>
                    <Button appearance="secondary" small onClick={(e) => { e.stopPropagation(); setActiveRow(row); }}>
                      詳細
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBase>

      {/* Drawer */}
      {activeRow && (
        <CoverageDrawer
          row={activeRow}
          ui={classifyRow(activeRow)}
          onClose={() => setActiveRow(null)}
          onApplied={() => { reload(); }}
        />
      )}

      {/* Modals (半自動処理機構) */}
      {windowModalOpen && (
        <PaymentWindowConfigModal
          onClose={() => setWindowModalOpen(false)}
          onChanged={() => { reload(); }}
        />
      )}
      {bulkOverrideOpen && resp?.summary?.bulk_action_counts?.active_window && (
        <BulkOverrideDialog
          window={resp.summary.bulk_action_counts.active_window}
          onClose={() => setBulkOverrideOpen(false)}
          onApplied={() => { reload(); }}
        />
      )}
      {bulkDispatchOpen && resp?.summary?.bulk_action_counts?.active_window && (
        <BulkDispatchDialog
          window={resp.summary.bulk_action_counts.active_window}
          onClose={() => setBulkDispatchOpen(false)}
          onApplied={() => { reload(); }}
        />
      )}
      {aricaReviewOpen && (
        <AricaDirectDebitReviewModal
          rows={(resp?.rows || []).filter((r) => r.bulk_action_eligibility?.is_arica_direct_debit_candidate)}
          onClose={() => setAricaReviewOpen(false)}
          onApplied={() => { reload(); }}
        />
      )}

      {/* selection 一括処理 progress modal (= 選択 → dispatch) */}
      {selectionBatchId && (
        <ProgressModal
          batchId={selectionBatchId}
          onClose={() => { setSelectionBatchId(null); reload(); }}
        />
      )}

      {/* selection floating action bar (= 選択 1 件以上で sticky bottom 表示) */}
      {selectedRowids.size > 0 && (
        <SelectionActionBar
          selectedCount={selectedRowids.size}
          offFilterCount={offFilterSelectedCount}
          busy={bulkBusy}
          error={bulkErr}
          toast={bulkToast}
          onClear={clearSelection}
          onBulk25th={onBulk25th}
          onBulkAutoDebit={onBulkAutoDebit}
          onBulkSkip={onBulkSkip}
          onBulkDispatch={onBulkDispatchSelected}
          onBulkDispatchV5={onBulkDispatchV5Selected}
          onCloseError={() => setBulkErr(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SelectAllCheckbox (= indeterminate を支援する小 component)
// ---------------------------------------------------------------------------

interface SelectAllCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled?: boolean;
  onChange: () => void;
}

const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({ checked, indeterminate, disabled, onChange }) => {
  const ref = React.useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      aria-label="filtered rows 全選択"
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  );
};

// ---------------------------------------------------------------------------
// SelectionActionBar (= sticky bottom floating bar)
// ---------------------------------------------------------------------------

interface SelectionActionBarProps {
  selectedCount: number;
  offFilterCount: number;
  busy: boolean;
  error: string | null;
  toast: string | null;
  onClear: () => void;
  onBulk25th: () => void;
  onBulkAutoDebit: () => void;
  onBulkSkip: () => void;
  onBulkDispatch: () => void;
  onBulkDispatchV5?: () => void;
  onCloseError: () => void;
}

const SelectionActionBar: React.FC<SelectionActionBarProps> = ({
  selectedCount, offFilterCount, busy, error, toast,
  onClear, onBulk25th, onBulkAutoDebit, onBulkSkip, onBulkDispatch, onBulkDispatchV5, onCloseError,
}) => {
  return (
    <div style={{
      position: 'fixed',
      left: 16, right: 16, bottom: 16,
      zIndex: 900,
      background: '#fff',
      border: '1px solid #2563c7',
      borderRadius: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,.18)',
      padding: '10px 14px',
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      {error && (
        <MessageBlock error onRequestClose={onCloseError}>{error}</MessageBlock>
      )}
      {toast && (
        <MessageBlock success>{toast}</MessageBlock>
      )}
      <HStack gap={0.5} alignItems="center" justifyContent="space-between">
        <HStack gap={0.5} alignItems="center">
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 13, fontWeight: 700, color: '#2563c7',
          }}>
            <MdCheckBox /> {selectedCount} 件選択中
          </span>
          {offFilterCount > 0 && (
            <span style={{ fontSize: 11, color: '#8a6a16' }}>
              (絞り込み外 {offFilterCount} 件含む)
            </span>
          )}
        </HStack>
        <HStack gap={0.5} alignItems="center">
          <Button appearance="secondary" small onClick={onClear} disabled={busy}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdClear /> 解除
            </span>
          </Button>
          <Button appearance="primary" small onClick={onBulk25th} disabled={busy}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdPlayArrow /> 25日払い ({selectedCount}件)
            </span>
          </Button>
          <Button appearance="secondary" small onClick={onBulkAutoDebit} disabled={busy}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdAccountBalance /> 引落 ({selectedCount}件)
            </span>
          </Button>
          <Button appearance="secondary" small onClick={onBulkSkip} disabled={busy}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdBlock /> skip ({selectedCount}件)
            </span>
          </Button>
          <Button appearance="primary" small onClick={onBulkDispatch} disabled={busy}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdSend /> dispatch ({selectedCount}件)
            </span>
          </Button>
          {onBulkDispatchV5 && (
            <Button appearance="primary" small onClick={onBulkDispatchV5} disabled={busy || selectedCount > 50}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#1e3a8a', color: 'white', padding: '0 6px', borderRadius: 4 }}>
                v5 dispatch ({selectedCount}件)
              </span>
            </Button>
          )}
        </HStack>
      </HStack>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Drawer (= right slide-in)
// ---------------------------------------------------------------------------

interface CoverageDrawerProps {
  row: CoverageRow;
  ui: UiStatus;
  onClose: () => void;
  onApplied: () => void;
}

const CoverageDrawer: React.FC<CoverageDrawerProps> = ({ row, ui, onClose, onApplied }) => {
  const cfg = UI_STATUS_CONFIG[ui];

  const onJumpToVerify = useCallback(() => {
    window.location.hash = '#invoice';
    onClose();
  }, [onClose]);

  const onJumpToAutoDebit = useCallback(() => {
    window.location.hash = '#bank_auto_debit';
    onClose();
  }, [onClose]);

  const onOpenFreee = useCallback(() => {
    if (!row.freee_deal_id) return;
    window.open(`https://accounts.secure.freee.co.jp/accounts/deals/${row.freee_deal_id}`, '_blank', 'noopener');
  }, [row.freee_deal_id]);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.3)', zIndex: 999,
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 'min(1100px, 96vw)', background: '#fff', zIndex: 1000,
        boxShadow: '-4px 0 24px rgba(0,0,0,.18)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Drawer header */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #ece9e6',
          display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between',
          background: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1c1916', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 380 }}>
              {row.partner_name || '(未設定)'}
            </span>
            {statusBadge(ui)}
            <span style={{ fontSize: 11, color: '#8a8580' }}>#{row.rowid}</span>
          </div>
          <Button appearance="secondary" small onClick={onClose}>閉じる</Button>
        </div>

        {/* Drawer body: grid 2 col (PDF | meta+CTA). minmax(0,*) prevents min-content blowout. */}
        <div style={{
          flex: 1, overflow: 'hidden', minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(360px, 1fr)',
        }}>
          {/* Left: PDF preview — fills available height */}
          <div style={{
            display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
            borderRight: '1px solid #ece9e6', background: '#fafaf9',
          }}>
            <div style={{
              padding: '8px 14px', borderBottom: '1px solid #ece9e6',
              fontSize: 12, fontWeight: 600, color: '#4a4540', background: '#f5f3f1',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span>証憑</span>
              <a
                href={row.pdf_url}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 11, color: '#2e7cf6', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 2 }}
              >
                新規 tab で開く <MdOpenInNew size={12} />
              </a>
            </div>
            <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden', position: 'relative' }}>
              <PdfViewer url={row.pdf_url} title={row.partner_name || `#${row.rowid}`} height="100%" />
            </div>
          </div>

          {/* Right: CTA (sticky top) + meta (scroll) */}
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
            {/* Sticky CTA bar */}
            <div style={{
              padding: '12px 16px', borderBottom: '1px solid #ece9e6', background: '#fff',
              display: 'flex', flexWrap: 'wrap', gap: 8,
            }}>
              {(ui === 'unprocessed' || ui === 'needs_fix') && (
                <Button appearance="primary" onClick={onJumpToVerify}>
                  仕訳検証 tab で処理する
                </Button>
              )}
              {ui === 'auto_debit' && (
                <Button appearance="primary" onClick={onJumpToAutoDebit}>
                  口座引落 page へ
                </Button>
              )}
              {ui === 'done' && row.freee_deal_id && (
                <Button appearance="primary" onClick={onOpenFreee}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    freee で確認 <MdOpenInNew />
                  </span>
                </Button>
              )}
              {ui === 'in_progress' && (
                <Button appearance="secondary" onClick={onJumpToVerify}>
                  仕訳検証 tab へ
                </Button>
              )}
            </div>

            {/* Scrolling meta column */}
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <VStack gap={1}>
                {/* Rejection reason */}
                {ui === 'rejected' && row.rejection_reason && (
                  <CardBase paddingSize="small">
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c33939', marginBottom: 4 }}>却下理由</div>
                    <div style={{ fontSize: 13, color: '#1c1916', lineHeight: 1.5 }}>{row.rejection_reason}</div>
                  </CardBase>
                )}

                {/* Meta data (金額は意図的に除外) */}
                <CardBase paddingSize="small">
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#4a4540', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #ece9e6' }}>
                    メタデータ
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 8, columnGap: 12, fontSize: 13 }}>
                    <div style={metaLabel}>受信日</div>
                    <div style={metaValue}>{row.receive_date || '—'}</div>
                    <div style={metaLabel}>発生日</div>
                    <div style={metaValue}>{row.issue_date || '—'}</div>
                    <div style={metaLabel}>支払期日</div>
                    <div style={metaValue}>{row.due_date || '—'}</div>
                    <div style={metaLabel}>請求書番号</div>
                    <div style={{ ...metaValue, fontFamily: 'ui-monospace, "SFMono-Regular", Menlo, monospace' }}>{row.invoice_number || '—'}</div>
                    <div style={metaLabel}>種別 (確定)</div>
                    <div style={metaValue}>
                      {row.override?.doc_type ? (
                        <span style={{
                          fontSize: 12, padding: '1px 6px', background: '#e8f0fe',
                          color: '#2563c7', border: '1px solid #a3c0f5', borderRadius: 3, fontWeight: 600,
                        }}>{row.override.doc_type}</span>
                      ) : (
                        <span style={{ color: '#c33939', fontWeight: 600 }}>未確認</span>
                      )}
                    </div>
                    <div style={metaLabel}>flow_type (確定)</div>
                    <div style={metaValue}>
                      {row.override?.flow_type ? (
                        <span style={{
                          fontSize: 12, padding: '1px 6px', background: '#e8f0fe',
                          color: '#2563c7', border: '1px solid #a3c0f5', borderRadius: 3, fontWeight: 600,
                        }}>{row.override.flow_type}</span>
                      ) : (
                        <span style={{ color: '#c33939', fontWeight: 600 }}>未確認</span>
                      )}
                    </div>
                    <div style={metaLabel}>取引先 ID</div>
                    <div style={metaValue}>
                      {row.override?.partner_id ? (
                        <span>
                          <span style={{ color: '#2563c7', fontWeight: 600 }}>{row.override.partner_id}</span>
                          {row.override.partner_name && (
                            <span style={{ color: '#6b6560', marginLeft: 6 }}>({row.override.partner_name})</span>
                          )}
                        </span>
                      ) : (
                        <span>{row.partner_id}</span>
                      )}
                    </div>
                    <div style={metaLabel}>D1 proposal</div>
                    <div style={metaValue}>{row.d1_proposal_id ?? '—'}</div>
                    <div style={metaLabel}>freee deal</div>
                    <div style={metaValue}>{row.freee_deal_id ?? '—'}</div>
                    <div style={metaLabel}>ref number</div>
                    <div style={metaValue}>{row.ref_number || '—'}</div>
                    <div style={metaLabel}>支払対象月</div>
                    <div style={metaValue}>{row.payment_target_estimate || '—'}</div>
                    <div style={metaLabel}>原状態</div>
                    <div style={metaValue}>
                      <code style={{ fontSize: 11, background: '#f5f3f2', padding: '1px 6px', borderRadius: 3, color: '#4a4540' }}>
                        {row.current_status}
                      </code>
                    </div>
                  </div>

                  {/* ARICA 推測 section (= grayed out、 信用しない) */}
                  <div style={{
                    marginTop: 12, paddingTop: 10, borderTop: '1px dashed #d8d5d2',
                  }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600, color: '#8a8580', marginBottom: 6,
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      <MdWarning style={{ fontSize: 13, color: '#bdb8b3' }} />
                      ARICA 推測 (参考: 信用しない)
                    </div>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 6, columnGap: 12,
                      fontSize: 12, color: '#8a8580',
                    }}>
                      <div style={metaLabelMuted}>ARICA 種別</div>
                      <div style={metaValueMuted}>
                        {row.arica_document_type || '—'}
                        {row.arica_document_format ? ` / ${row.arica_document_format}` : ''}
                      </div>
                      <div style={metaLabelMuted}>ARICA 支払方法</div>
                      <div style={metaValueMuted}>{row.arica_payment_method || row.payment_method || '—'}</div>
                      <div style={metaLabelMuted}>ARICA partner_id</div>
                      <div style={metaValueMuted}>
                        {row.arica_partner_id ?? row.partner_id ?? '—'}
                        {row.arica_partner_name && (
                          <span style={{ marginLeft: 6 }}>({row.arica_partner_name})</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardBase>

                {/* Override action panel */}
                <OverrideActionPanel row={row} onApplied={onApplied} />
              </VStack>
            </div>
          </div>
        </div>

        {/* Footer just shows cfg color band so user has a state cue at bottom */}
        <div style={{
          height: 6, background: cfg.bg, borderTop: `1px solid ${cfg.border}`,
        }} />
      </div>
    </>
  );
};

const metaLabel: React.CSSProperties = { color: '#8a8580', fontSize: 12, alignSelf: 'center' };
const metaValue: React.CSSProperties = { color: '#1c1916', fontSize: 13, wordBreak: 'break-all' };
const metaLabelMuted: React.CSSProperties = { color: '#bdb8b3', fontSize: 11, alignSelf: 'center' };
const metaValueMuted: React.CSSProperties = { color: '#8a8580', fontSize: 12, wordBreak: 'break-all' };

// ---------------------------------------------------------------------------
// Cell styles
// ---------------------------------------------------------------------------

const cellHead: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: '#6b6560',
  borderBottom: '1px solid #ece9e6',
  whiteSpace: 'nowrap',
};

const cellBody: React.CSSProperties = {
  padding: '8px 12px',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

// ---------------------------------------------------------------------------
// OverrideActionPanel
// ---------------------------------------------------------------------------

interface OverrideActionPanelProps {
  row: CoverageRow;
  onApplied: () => void;
}

type DocTypeChoice = 'invoice' | 'receipt' | 'quotation' | 'order' | 'other' | 'clear';

interface PartnerCandidate {
  partner_id: number;
  partner_name: string;
  pattern_status?: string | null;
  hits?: number | null;
  score?: number | null;
}

const OverrideActionPanel: React.FC<OverrideActionPanelProps> = ({ row, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [docChoice, setDocChoice] = useState<DocTypeChoice>(
    (row.override?.doc_type as DocTypeChoice | undefined) || 'invoice'
  );
  const [partnerQuery, setPartnerQuery] = useState<string>('');
  const [partnerCandidates, setPartnerCandidates] = useState<PartnerCandidate[]>([]);
  const [partnerLoading, setPartnerLoading] = useState(false);

  const sessionId = getSessionId();
  const sessionMissing = !sessionId;

  const flushMessages = useCallback(() => {
    setErr(null);
    setInfo(null);
  }, []);

  const post = useCallback(async (body: Record<string, unknown>): Promise<void> => {
    flushMessages();
    if (!sessionId) {
      setErr('session_id 未取得です');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/invoice-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rowid: row.rowid, session_id: sessionId, ...body }),
      });
      if (!r.ok) {
        const j: unknown = await r.json().catch(() => null);
        const message =
          j && typeof j === 'object' && 'error' in j ? String((j as { error: unknown }).error) : `HTTP ${r.status}`;
        throw new Error(message);
      }
      setInfo('適用しました');
      onApplied();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [row.rowid, sessionId, onApplied, flushMessages]);

  const remove = useCallback(async (): Promise<void> => {
    flushMessages();
    if (!sessionId) {
      setErr('session_id 未取得です');
      return;
    }
    if (!window.confirm('この行の override を完全に解除します。 よろしいですか?')) return;
    setBusy(true);
    try {
      const u = new URL(`${API_BASE}/api/admin/invoice-override`);
      u.searchParams.set('rowid', String(row.rowid));
      u.searchParams.set('session_id', sessionId);
      const r = await fetch(u.toString(), { method: 'DELETE' });
      if (!r.ok) {
        const j: unknown = await r.json().catch(() => null);
        const message =
          j && typeof j === 'object' && 'error' in j ? String((j as { error: unknown }).error) : `HTTP ${r.status}`;
        throw new Error(message);
      }
      setInfo('override を解除しました');
      onApplied();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [row.rowid, sessionId, onApplied, flushMessages]);

  const confirmAndPost = useCallback(async (
    label: string,
    body: Record<string, unknown>,
  ): Promise<void> => {
    if (!window.confirm(`${label} を適用しますか?`)) return;
    const reason = window.prompt('理由 (任意):') || undefined;
    await post({ ...body, ...(reason ? { reason } : {}) });
  }, [post]);

  const setFlowType = useCallback(async (flowType: 'bank_auto_debit' | '25th_payment') => {
    const lbl = flowType === 'bank_auto_debit' ? 'これは引落' : 'これは引落ではない (25日払い)';
    await confirmAndPost(lbl, { flow_type: flowType });
  }, [confirmAndPost]);

  const toggleSkip = useCallback(async () => {
    const next = !row.override?.skip;
    const lbl = next ? 'この行を skip 扱い' : 'skip を解除';
    await confirmAndPost(lbl, { skip: next });
  }, [row.override?.skip, confirmAndPost]);

  const applyDocType = useCallback(async () => {
    if (docChoice === 'clear') {
      await confirmAndPost('種別 override を解除', { doc_type: null });
    } else {
      await confirmAndPost(`種別 = ${docChoice}`, { doc_type: docChoice });
    }
  }, [docChoice, confirmAndPost]);

  const searchPartners = useCallback(async () => {
    flushMessages();
    setPartnerLoading(true);
    try {
      const u = new URL(`${API_BASE}/api/admin/orphan-partner-suggest`);
      u.searchParams.set('arica_rowid', String(row.rowid));
      const r = await fetch(u.toString());
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j: { candidates?: PartnerCandidate[] } = await r.json();
      let list = Array.isArray(j.candidates) ? j.candidates : [];
      const q = partnerQuery.trim();
      if (q) {
        const ql = q.toLowerCase();
        list = list.filter((c) =>
          (c.partner_name || '').toLowerCase().includes(ql) ||
          String(c.partner_id) === q
        );
      }
      setPartnerCandidates(list.slice(0, 10));
      if (list.length === 0) setInfo('候補なし (rowid 由来 hint で 0 件)');
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPartnerLoading(false);
    }
  }, [row.rowid, partnerQuery, flushMessages]);

  const choosePartner = useCallback(async (c: PartnerCandidate) => {
    await confirmAndPost(
      `取引先 = ${c.partner_name} (#${c.partner_id})`,
      { partner_id: c.partner_id, partner_name: c.partner_name },
    );
    setPartnerCandidates([]);
    setPartnerQuery('');
  }, [confirmAndPost]);

  return (
    <CardBase paddingSize="small">
      <div style={{
        fontSize: 12, fontWeight: 700, color: '#2563c7', marginBottom: 8,
        paddingBottom: 6, borderBottom: '1px solid #ece9e6',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <MdEdit /> Override 操作 (= user 確定値)
      </div>

      {sessionMissing && (
        <div style={{ marginBottom: 8 }}>
          <Note>session_id 未取得です。 admin session を open してから操作してください。</Note>
        </div>
      )}

      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {info && !err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock success>{info}</MessageBlock>
        </div>
      )}

      <VStack gap={1}>
        {/* flow_type */}
        <div>
          <div style={overrideSubLabel}>flow_type</div>
          <HStack gap={0.5}>
            <Button
              appearance={row.override?.flow_type === 'bank_auto_debit' ? 'primary' : 'secondary'}
              small
              disabled={busy || sessionMissing}
              onClick={() => setFlowType('bank_auto_debit')}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdSwapHoriz /> これは引落
              </span>
            </Button>
            <Button
              appearance={row.override?.flow_type === '25th_payment' ? 'primary' : 'secondary'}
              small
              disabled={busy || sessionMissing}
              onClick={() => setFlowType('25th_payment')}
            >
              これは引落ではない
            </Button>
          </HStack>
        </div>

        {/* skip */}
        <div>
          <div style={overrideSubLabel}>skip 状態</div>
          <Button
            appearance={row.override?.skip ? 'primary' : 'secondary'}
            small
            disabled={busy || sessionMissing}
            onClick={toggleSkip}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdBlock /> {row.override?.skip ? 'skip 解除' : 'skip にする'}
            </span>
          </Button>
        </div>

        {/* doc_type */}
        <div>
          <div style={overrideSubLabel}>種別変更</div>
          <HStack gap={0.5} alignItems="center">
            <SelectBox
              value={docChoice}
              onChange={(e) => setDocChoice(e.target.value as DocTypeChoice)}
              options={[
                { name: 'invoice', value: 'invoice' },
                { name: 'receipt', value: 'receipt' },
                { name: 'quotation', value: 'quotation' },
                { name: 'order', value: 'order' },
                { name: 'other', value: 'other' },
                { name: '解除', value: 'clear' },
              ]}
              width="small"
              small
              label="種別"
            />
            <Button
              appearance="secondary"
              small
              disabled={busy || sessionMissing}
              onClick={applyDocType}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdLabel /> 適用
              </span>
            </Button>
          </HStack>
        </div>

        {/* partner override */}
        <div>
          <div style={overrideSubLabel}>取引先 訂正</div>
          <HStack gap={0.5} alignItems="center">
            <SearchField
              value={partnerQuery}
              onChange={(e) => setPartnerQuery(e.target.value)}
              placeholder="絞り込み (任意)"
              width="medium"
              small
            />
            <Button
              appearance="secondary"
              small
              disabled={busy || partnerLoading}
              onClick={searchPartners}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdPerson /> {partnerLoading ? '検索中…' : '候補取得'}
              </span>
            </Button>
          </HStack>
          {partnerCandidates.length > 0 && (
            <div style={{ marginTop: 6, border: '1px solid #ece9e6', borderRadius: 4, maxHeight: 180, overflow: 'auto' }}>
              {partnerCandidates.map((c) => (
                <div
                  key={c.partner_id}
                  style={{
                    padding: '6px 10px', borderBottom: '1px solid #f3f1ee',
                    cursor: busy || sessionMissing ? 'not-allowed' : 'pointer',
                    fontSize: 12, color: busy || sessionMissing ? '#bdb8b3' : '#1c1916',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                  onClick={() => { if (!busy && !sessionMissing) void choosePartner(c); }}
                >
                  <span>
                    <span style={{ fontWeight: 600 }}>{c.partner_name}</span>
                    <span style={{ color: '#8a8580', marginLeft: 6 }}>#{c.partner_id}</span>
                  </span>
                  {typeof c.hits === 'number' && (
                    <span style={{ fontSize: 11, color: '#8a8580' }}>{c.hits} hit</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* clear all */}
        {row.override && (
          <div style={{ marginTop: 4, paddingTop: 8, borderTop: '1px dashed #d8d5d2' }}>
            <Button
              appearance="secondary"
              small
              disabled={busy || sessionMissing}
              onClick={remove}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdRestore /> override 全解除
              </span>
            </Button>
            {row.override.updated_at && (
              <Note>
                最終更新: {row.override.updated_at}
                {row.override.updated_by ? ` by ${row.override.updated_by}` : ''}
                {row.override.reason ? ` / 理由: ${row.override.reason}` : ''}
              </Note>
            )}
          </div>
        )}
      </VStack>
    </CardBase>
  );
};

const overrideSubLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: '#6b6560', marginBottom: 4,
};

// ===========================================================================
// BulkActionBar (= 状態カード grid 直下に挿入)
// ===========================================================================

interface BulkActionBarProps {
  activeWindow: { id: number; label: string; target_payment_month: string } | null;
  counts: {
    eligible_for_bulk_override: number;
    eligible_for_bulk_dispatch: number;
    arica_direct_debit_candidates: number;
    active_window: { id: number; label: string; target_payment_month: string } | null;
  };
  onOpenWindowConfig: () => void;
  onBulkOverride: () => void;
  onBulkDispatch: () => void;
  onReviewAricaDirectDebit: () => void;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
  activeWindow,
  counts,
  onOpenWindowConfig,
  onBulkOverride,
  onBulkDispatch,
  onReviewAricaDirectDebit,
}) => {
  const sessionId = getSessionId();
  const sessionMissing = !sessionId;
  const noWindow = !activeWindow;

  return (
    <CardBase paddingSize="small" mb={0.5}>
      <HStack gap={0.5} alignItems="center" justifyContent="space-between">
        <HStack gap={0.5} alignItems="center">
          <Button appearance="secondary" small onClick={onOpenWindowConfig}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdSettings /> 支払 window 設定
            </span>
          </Button>
          {activeWindow ? (
            <span style={{
              fontSize: 12, padding: '2px 8px', background: '#e8f0fe',
              color: '#2563c7', border: '1px solid #a3c0f5', borderRadius: 3,
              fontWeight: 500,
            }}>
              active: {activeWindow.label} ({activeWindow.target_payment_month})
            </span>
          ) : (
            <span style={{
              fontSize: 12, padding: '2px 8px', background: '#fdecec',
              color: '#c33939', border: '1px solid #f5a3a3', borderRadius: 3,
              fontWeight: 500,
            }}>
              active window 未設定
            </span>
          )}
        </HStack>
        <HStack gap={0.5} alignItems="center">
          <Button
            appearance="primary"
            small
            disabled={sessionMissing || noWindow || counts.eligible_for_bulk_override === 0}
            onClick={onBulkOverride}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdPlayArrow /> 未処理 → 25日払い 一括確定 ({counts.eligible_for_bulk_override}件)
            </span>
          </Button>
          <Button
            appearance="primary"
            small
            disabled={sessionMissing || noWindow || counts.eligible_for_bulk_dispatch === 0}
            onClick={onBulkDispatch}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdSend /> 確定済 → 一括 dispatch ({counts.eligible_for_bulk_dispatch}件)
            </span>
          </Button>
          <Button
            appearance="secondary"
            small
            disabled={sessionMissing || counts.arica_direct_debit_candidates === 0}
            onClick={onReviewAricaDirectDebit}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdAccountBalance /> ARICA 推測 引落 候補 確認 ({counts.arica_direct_debit_candidates}件)
            </span>
          </Button>
        </HStack>
      </HStack>
      {sessionMissing && (
        <div style={{ marginTop: 8 }}>
          <Note>session_id 未取得です。 admin session を open してから操作してください。</Note>
        </div>
      )}
    </CardBase>
  );
};

// ===========================================================================
// Modal shell (= 共通)
// ===========================================================================

interface ModalShellProps {
  title: string;
  onClose: () => void;
  width?: number | string;
  children: React.ReactNode;
}

const ModalShell: React.FC<ModalShellProps> = ({ title, onClose, width = 720, children }) => {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1100,
        }}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: `min(${typeof width === 'number' ? `${width}px` : width}, 96vw)`,
        maxHeight: '90vh',
        background: '#fff', zIndex: 1101,
        borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,.24)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid #ece9e6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#1c1916' }}>{title}</span>
          <Button appearance="secondary" small onClick={onClose}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdClose /> 閉じる
            </span>
          </Button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {children}
        </div>
      </div>
    </>
  );
};

// ===========================================================================
// PaymentWindowConfigModal
// ===========================================================================

interface PaymentWindowConfigModalProps {
  onClose: () => void;
  onChanged: () => void;
}

const PaymentWindowConfigModal: React.FC<PaymentWindowConfigModalProps> = ({ onClose, onChanged }) => {
  const [items, setItems] = useState<PaymentWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<PaymentWindowOverlapConflict[]>([]);
  // new form
  const [newLabel, setNewLabel] = useState('');
  const [newFrom, setNewFrom] = useState('');
  const [newTo, setNewTo] = useState('');
  const [newTargetMonth, setNewTargetMonth] = useState('');
  const [newFlowType, setNewFlowType] = useState<'25th_payment' | 'bank_auto_debit' | 'other'>('25th_payment');
  const [submitting, setSubmitting] = useState(false);

  const sessionId = getSessionId();

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetchPaymentWindows(false);
      setItems(r.items || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onSubmit = useCallback(async () => {
    if (!sessionId) { setErr('session_id 未取得'); return; }
    if (!newLabel || !newFrom || !newTo || !newTargetMonth) {
      setErr('全 field 必須');
      return;
    }
    setSubmitting(true);
    setErr(null);
    setConflicts([]);
    try {
      const r = await createPaymentWindow({
        label: newLabel,
        receive_from: newFrom,
        receive_to: newTo,
        target_payment_month: newTargetMonth,
        flow_type: newFlowType,
        session_id: sessionId,
      });
      if (!r.ok) {
        if (r.conflicts && r.conflicts.length > 0) {
          setConflicts(r.conflicts);
          setErr('期間が既存 active window と重複しています');
        } else {
          setErr(r.error || '作成失敗');
        }
        return;
      }
      setNewLabel(''); setNewFrom(''); setNewTo(''); setNewTargetMonth('');
      await reload();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, newLabel, newFrom, newTo, newTargetMonth, newFlowType, reload, onChanged]);

  const onToggleActive = useCallback(async (it: PaymentWindow) => {
    if (!sessionId) { setErr('session_id 未取得'); return; }
    const next = it.is_active ? 0 : 1;
    if (!window.confirm(`${it.label} を ${next ? '有効化' : '無効化'} しますか?`)) return;
    setErr(null);
    setConflicts([]);
    try {
      const r = await updatePaymentWindow(it.id, { is_active: next, session_id: sessionId });
      if (!r.ok) {
        if (r.conflicts && r.conflicts.length > 0) {
          setConflicts(r.conflicts);
          setErr('期間重複のため有効化できません');
        } else {
          setErr(r.error || '更新失敗');
        }
        return;
      }
      await reload();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || String(e));
    }
  }, [sessionId, reload, onChanged]);

  const onDelete = useCallback(async (it: PaymentWindow) => {
    if (!sessionId) { setErr('session_id 未取得'); return; }
    if (!window.confirm(`${it.label} を 物理削除 しますか? (取消不可)`)) return;
    setErr(null);
    try {
      await deletePaymentWindow(it.id, true, sessionId);
      await reload();
      onChanged();
    } catch (e: any) {
      setErr(e?.message || String(e));
    }
  }, [sessionId, reload, onChanged]);

  return (
    <ModalShell title="支払 window 設定" onClose={onClose} width={860}>
      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {conflicts.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <CardBase paddingSize="small">
            <Text size={0.875} weight="bold">重複する active window:</Text>
            <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#4a4540' }}>
              {conflicts.map((c) => (
                <li key={c.id}>
                  #{c.id} {c.label} ({c.receive_from} 〜 {c.receive_to} → {c.target_payment_month})
                </li>
              ))}
            </ul>
          </CardBase>
        </div>
      )}

      {/* 新規追加 form */}
      <CardBase paddingSize="small">
        <Text size={0.875} weight="bold">新規追加</Text>
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: 8, alignItems: 'end' }}>
          <div>
            <div style={overrideSubLabel}>label</div>
            <input
              type="text" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
              placeholder="2026-05 払い"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={overrideSubLabel}>receive_from</div>
            <input
              type="date" value={newFrom} onChange={(e) => setNewFrom(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={overrideSubLabel}>receive_to</div>
            <input
              type="date" value={newTo} onChange={(e) => setNewTo(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <div style={overrideSubLabel}>target_payment_month</div>
            <input
              type="text" value={newTargetMonth} onChange={(e) => setNewTargetMonth(e.target.value)}
              placeholder="2026-05"
              style={inputStyle}
            />
          </div>
          <div>
            <div style={overrideSubLabel}>flow_type</div>
            <SelectBox
              value={newFlowType}
              onChange={(e) => setNewFlowType(e.target.value as any)}
              options={[
                { name: '25th_payment', value: '25th_payment' },
                { name: 'bank_auto_debit', value: 'bank_auto_debit' },
                { name: 'other', value: 'other' },
              ]}
              width="small"
              small
            />
          </div>
          <Button appearance="primary" small onClick={onSubmit} disabled={submitting}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdAdd /> 追加
            </span>
          </Button>
        </div>
      </CardBase>

      {/* 一覧 */}
      <div style={{ marginTop: 12 }}>
        <Text size={0.875} weight="bold">一覧 (過去 = grayed out)</Text>
        {loading ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#6b6560' }}>読込中…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: '#6b6560' }}>登録なし</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
            <thead>
              <tr style={{ background: '#f8f6f4' }}>
                <th style={cellHead}>#</th>
                <th style={cellHead}>label</th>
                <th style={cellHead}>receive_from</th>
                <th style={cellHead}>receive_to</th>
                <th style={cellHead}>target_month</th>
                <th style={cellHead}>flow_type</th>
                <th style={cellHead}>active</th>
                <th style={{ ...cellHead, textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{
                  borderBottom: '1px solid #ece9e6',
                  background: it.is_active ? '#fff' : '#f8f6f4',
                  color: it.is_active ? '#1c1916' : '#8a8580',
                }}>
                  <td style={cellBody}>{it.id}</td>
                  <td style={cellBody}>{it.label}</td>
                  <td style={cellBody}>{it.receive_from}</td>
                  <td style={cellBody}>{it.receive_to}</td>
                  <td style={cellBody}>{it.target_payment_month}</td>
                  <td style={cellBody}>{it.flow_type}</td>
                  <td style={cellBody}>{it.is_active ? '有効' : '無効'}</td>
                  <td style={{ ...cellBody, textAlign: 'right' }}>
                    <HStack gap={0.5} alignItems="center" justifyContent="end">
                      <Button appearance="secondary" small onClick={() => onToggleActive(it)}>
                        {it.is_active ? '無効化' : '有効化'}
                      </Button>
                      <Button appearance="secondary" small onClick={() => onDelete(it)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <MdDelete /> 削除
                        </span>
                      </Button>
                    </HStack>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </ModalShell>
  );
};

const inputStyle: React.CSSProperties = {
  padding: '4px 6px', border: '1px solid #dbd9d7', borderRadius: 4,
  fontSize: 13, fontFamily: 'inherit', width: '100%',
};

// ===========================================================================
// BulkOverrideDialog
// ===========================================================================

interface BulkOverrideDialogProps {
  window: { id: number; label: string; target_payment_month: string };
  onClose: () => void;
  onApplied: () => void;
}

const BulkOverrideDialog: React.FC<BulkOverrideDialogProps> = ({ window: wnd, onClose, onApplied }) => {
  const [preview, setPreview] = useState<BulkOverrideByWindowResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [includeAricaDirectDebit, setIncludeAricaDirectDebit] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [done, setDone] = useState(false);

  const sessionId = getSessionId();

  const fetchPreview = useCallback(async (includeArica: boolean) => {
    if (!sessionId) { setErr('session_id 未取得'); setLoading(false); return; }
    setLoading(true);
    setErr(null);
    try {
      const r = await bulkOverrideByWindow({
        window_id: wnd.id,
        flow_type: '25th_payment',
        dry_run: true,
        exclude_arica_direct_debit: !includeArica,
        session_id: sessionId,
      });
      setPreview(r);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId, wnd.id]);

  useEffect(() => { fetchPreview(includeAricaDirectDebit); }, [fetchPreview, includeAricaDirectDebit]);

  const onExecute = useCallback(async () => {
    if (!sessionId || !preview) return;
    const n = preview.preview.eligible_for_override;
    if (n === 0) { setErr('対象 0 件'); return; }
    if (!window.confirm(`${n} 件を 25日払い 確定として一括 override しますか?`)) return;
    setExecuting(true);
    setErr(null);
    try {
      const r = await bulkOverrideByWindow({
        window_id: wnd.id,
        flow_type: '25th_payment',
        dry_run: false,
        exclude_arica_direct_debit: !includeAricaDirectDebit,
        session_id: sessionId,
      });
      setPreview(r);
      setDone(true);
      onApplied();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setExecuting(false);
    }
  }, [sessionId, preview, wnd.id, includeAricaDirectDebit, onApplied]);

  return (
    <ModalShell title={`一括 override (${wnd.label} → ${wnd.target_payment_month})`} onClose={onClose} width={640}>
      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {done && preview?.applied && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock success>{preview.applied.inserted} 件を確定しました</MessageBlock>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#6b6560' }}>preview 取得中…</div>
      ) : preview ? (
        <VStack gap={1}>
          <CardBase paddingSize="small">
            <Text size={0.875} weight="bold">preview (dry_run)</Text>
            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 6, fontSize: 13 }}>
              <div style={metaLabel}>window 内 ARICA</div>
              <div style={metaValue}>{preview.preview.total_in_window} 件</div>
              <div style={metaLabel}>(a) 一括確定対象</div>
              <div style={{ ...metaValue, color: '#287a48', fontWeight: 700 }}>{preview.preview.eligible_for_override} 件</div>
              <div style={metaLabel}>(b) 既 override (skip)</div>
              <div style={metaValueMuted}>{preview.preview.skip_already_overridden} 件</div>
              <div style={metaLabel}>(c) 既処理 (skip)</div>
              <div style={metaValueMuted}>{preview.preview.skip_processed} 件</div>
              <div style={metaLabel}>(d) ARICA 推測 引落 (skip)</div>
              <div style={metaValueMuted}>{preview.preview.skip_arica_direct_debit} 件</div>
            </div>
          </CardBase>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={includeAricaDirectDebit}
              onChange={(e) => setIncludeAricaDirectDebit(e.target.checked)}
              disabled={executing}
            />
            ARICA 推測 引落 も含める (危険: user 確認 modal で個別判定推奨)
          </label>
          {!done && (
            <Button
              appearance="primary"
              onClick={onExecute}
              disabled={executing || preview.preview.eligible_for_override === 0}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <MdPlayArrow /> {preview.preview.eligible_for_override} 件を 25日払い 確定
              </span>
            </Button>
          )}
          {done && (
            <Button appearance="secondary" onClick={onClose}>閉じる</Button>
          )}
        </VStack>
      ) : null}
    </ModalShell>
  );
};

// ===========================================================================
// BulkDispatchDialog + ProgressModal
// ===========================================================================

interface BulkDispatchDialogProps {
  window: { id: number; label: string; target_payment_month: string };
  onClose: () => void;
  onApplied: () => void;
}

const BulkDispatchDialog: React.FC<BulkDispatchDialogProps> = ({ window: wnd, onClose, onApplied }) => {
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [concurrency, setConcurrency] = useState(3);
  const [executing, setExecuting] = useState(false);

  const sessionId = getSessionId();

  const fetchPreview = useCallback(async () => {
    if (!sessionId) { setErr('session_id 未取得'); setLoading(false); return; }
    setLoading(true);
    setErr(null);
    try {
      const r = await startBulkDispatch({
        window_id: wnd.id,
        flow_type: '25th_payment',
        concurrency,
        dry_run: true,
        session_id: sessionId,
      });
      setPreviewTotal(r.total);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId, wnd.id, concurrency]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const onExecute = useCallback(async () => {
    if (!sessionId) return;
    if (previewTotal === null || previewTotal === 0) { setErr('対象 0 件'); return; }
    if (!window.confirm(`${previewTotal} 件を concurrency=${concurrency} で dispatch します。 よろしいですか?`)) return;
    setExecuting(true);
    setErr(null);
    try {
      const r = await startBulkDispatch({
        window_id: wnd.id,
        flow_type: '25th_payment',
        concurrency,
        dry_run: false,
        session_id: sessionId,
      });
      if (r.batch_id) {
        setBatchId(r.batch_id);
      } else {
        setErr('batch_id 取得失敗');
      }
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setExecuting(false);
    }
  }, [sessionId, previewTotal, concurrency, wnd.id]);

  if (batchId) {
    return (
      <ProgressModal
        batchId={batchId}
        onClose={() => { onClose(); onApplied(); }}
      />
    );
  }

  return (
    <ModalShell title={`一括 dispatch (${wnd.label})`} onClose={onClose} width={560}>
      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#6b6560' }}>preview 取得中…</div>
      ) : (
        <VStack gap={1}>
          <CardBase paddingSize="small">
            <Text size={0.875} weight="bold">preview (dry_run)</Text>
            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 6, fontSize: 13 }}>
              <div style={metaLabel}>対象 (確定済 + 未処理)</div>
              <div style={{ ...metaValue, color: '#287a48', fontWeight: 700 }}>{previewTotal ?? '—'} 件</div>
              <div style={metaLabel}>concurrency</div>
              <div style={metaValue}>
                <SelectBox
                  value={String(concurrency)}
                  onChange={(e) => setConcurrency(Number(e.target.value))}
                  options={[
                    { name: '1', value: '1' },
                    { name: '2', value: '2' },
                    { name: '3 (default)', value: '3' },
                    { name: '4 (max)', value: '4' },
                  ]}
                  width="small"
                  small
                />
              </div>
            </div>
          </CardBase>
          <Button
            appearance="primary"
            onClick={onExecute}
            disabled={executing || previewTotal === null || previewTotal === 0}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MdSend /> dispatch 開始 ({previewTotal ?? 0} 件)
            </span>
          </Button>
        </VStack>
      )}
    </ModalShell>
  );
};

interface ProgressModalProps {
  batchId: string;
  onClose: () => void;
}

const ProgressModal: React.FC<ProgressModalProps> = ({ batchId, onClose }) => {
  const [status, setStatus] = useState<BulkDispatchBatchStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [aborting, setAborting] = useState(false);
  const [autoResumeCount, setAutoResumeCount] = useState(0);

  const sessionId = getSessionId();
  const prevDoneRef = useRef<number>(-1);
  const stallCountRef = useRef<number>(0);
  const lastResumeAtRef = useRef<number>(0);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      try {
        const r = await getBulkDispatchStatus(batchId, true);
        if (stopped) return;
        setStatus(r);
        if (r.batch.status === 'finished' || r.batch.status === 'aborted') {
          stopped = true;
          return;
        }
        // stall 検出 → auto-resume
        //   (= worker invocation 5min wall clock 制限で stuck した場合の自動復旧)
        const done = (r.batch.succeeded || 0) + (r.batch.failed || 0);
        const hasRemaining = (r.batch.total || 0) > done;
        if (!hasRemaining) return;
        if (done === prevDoneRef.current) {
          stallCountRef.current += 1;
        } else {
          stallCountRef.current = 0;
          prevDoneRef.current = done;
        }
        // stall 連続 3 tick (= ~9s 進捗なし) で resume
        //   かつ 直近 resume から 10s 以上経過しているなら fire (= 連打回避)
        const now = Date.now();
        if (stallCountRef.current >= 3 && (now - lastResumeAtRef.current) > 10000) {
          if (!sessionId) return;
          lastResumeAtRef.current = now;
          stallCountRef.current = 0;
          try {
            await resumeBulkDispatch(batchId, sessionId);
            if (!stopped) setAutoResumeCount((c) => c + 1);
          } catch (e: any) {
            if (!stopped) setErr(`auto-resume failed: ${e?.message || String(e)}`);
          }
        }
      } catch (e: any) {
        if (!stopped) setErr(e?.message || String(e));
      }
    };
    tick();
    const t = setInterval(() => {
      if (stopped) { clearInterval(t); return; }
      tick();
    }, 3000);
    return () => { stopped = true; clearInterval(t); };
  }, [batchId, sessionId]);

  const onAbort = useCallback(async () => {
    if (!sessionId) { setErr('session_id 未取得'); return; }
    if (!window.confirm('dispatch を中止しますか? (残 pending は skipped に flip)')) return;
    setAborting(true);
    setErr(null);
    try {
      await abortBulkDispatch(batchId, sessionId);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setAborting(false);
    }
  }, [batchId, sessionId]);

  const b = status?.batch;
  const items = status?.items || [];
  const failedItems = items.filter((it) => it.status === 'failed');
  const finished = b?.status === 'finished' || b?.status === 'aborted';
  const total = b?.total ?? 0;
  const done = (b?.succeeded ?? 0) + (b?.failed ?? 0);
  const remaining = Math.max(total - done - (b?.in_progress ?? 0), 0);

  return (
    <ModalShell title={`dispatch 進捗 (${batchId})`} onClose={onClose} width={760}>
      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {b ? (
        <VStack gap={1}>
          <CardBase paddingSize="small">
            <HStack gap={1} alignItems="center" justifyContent="space-between">
              <div style={{ flex: 1 }}>
                <ProgressBar
                  value={done}
                  maxValue={total}
                  width="full"
                  ariaLabel="dispatch 進捗"
                />
              </div>
              <Text size={0.875} color="burnt">
                {done} / {total} ({total > 0 ? Math.round((done / total) * 100) : 0}%)
              </Text>
            </HStack>
            <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, fontSize: 12 }}>
              <div><span style={metaLabel}>状態</span><br />
                <strong style={{ color: finished ? (b.status === 'finished' ? '#287a48' : '#8a6a16') : '#2563c7' }}>{b.status}</strong>
              </div>
              <div><span style={metaLabel}>成功</span><br /><strong style={{ color: '#287a48' }}>{b.succeeded}</strong></div>
              <div><span style={metaLabel}>失敗</span><br /><strong style={{ color: '#c33939' }}>{b.failed}</strong></div>
              <div><span style={metaLabel}>処理中</span><br /><strong>{b.in_progress}</strong></div>
              <div><span style={metaLabel}>残</span><br /><strong>{remaining}</strong></div>
            </div>
            {autoResumeCount > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#8a6a16' }}>
                auto-resume 発火: {autoResumeCount} 回 (= worker invocation 5min 制限の自動復旧)
              </div>
            )}
          </CardBase>

          {failedItems.length > 0 && (
            <CardBase paddingSize="small">
              <Text size={0.875} weight="bold">失敗 ({failedItems.length} 件)</Text>
              <div style={{ marginTop: 8, maxHeight: 220, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8f6f4' }}>
                      <th style={cellHead}>rowid</th>
                      <th style={cellHead}>stage</th>
                      <th style={cellHead}>attempts</th>
                      <th style={cellHead}>error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failedItems.map((it) => (
                      <tr key={it.id} style={{ borderBottom: '1px solid #ece9e6' }}>
                        <td style={cellBody}>#{it.rowid}</td>
                        <td style={cellBody}>{it.stage || '—'}</td>
                        <td style={cellBody}>{it.attempts}</td>
                        <td style={{ ...cellBody, whiteSpace: 'normal', color: '#c33939', fontSize: 11 }}>{it.error_message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBase>
          )}

          <HStack gap={0.5}>
            {!finished && (
              <Button appearance="secondary" onClick={onAbort} disabled={aborting}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <MdStop /> 中止
                </span>
              </Button>
            )}
            {finished && (
              <Button appearance="primary" onClick={onClose}>閉じる</Button>
            )}
          </HStack>
        </VStack>
      ) : (
        <div style={{ padding: 16, textAlign: 'center', color: '#6b6560' }}>status 取得中…</div>
      )}
    </ModalShell>
  );
};

// ===========================================================================
// AricaDirectDebitReviewModal
// ===========================================================================

interface AricaDirectDebitReviewModalProps {
  rows: CoverageRow[];
  onClose: () => void;
  onApplied: () => void;
}

const AricaDirectDebitReviewModal: React.FC<AricaDirectDebitReviewModalProps> = ({ rows, onClose, onApplied }) => {
  const [busyRowid, setBusyRowid] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<Set<number>>(new Set());

  const sessionId = getSessionId();

  const apply = useCallback(async (rowid: number, flowType: 'bank_auto_debit' | '25th_payment') => {
    if (!sessionId) { setErr('session_id 未取得'); return; }
    setBusyRowid(rowid);
    setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/invoice-override`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowid,
          session_id: sessionId,
          flow_type: flowType,
          reason: `arica_direct_debit_review:${flowType}`,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error((j as any)?.error || `HTTP ${r.status}`);
      }
      setDone((prev) => {
        const next = new Set(prev);
        next.add(rowid);
        return next;
      });
      onApplied();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setBusyRowid(null);
    }
  }, [sessionId, onApplied]);

  const remaining = rows.filter((r) => !done.has(r.rowid));

  return (
    <ModalShell title={`ARICA 推測 引落 候補 確認 (${remaining.length} / ${rows.length} 件)`} onClose={onClose} width={860}>
      {err && (
        <div style={{ marginBottom: 8 }}>
          <MessageBlock error>{err}</MessageBlock>
        </div>
      )}
      {remaining.length === 0 ? (
        <div style={{ padding: 16, textAlign: 'center', color: '#287a48' }}>
          <MdCheckCircle /> 全件 確認済
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f6f4' }}>
              <th style={cellHead}>rowid</th>
              <th style={cellHead}>業者名</th>
              <th style={cellHead}>受信日</th>
              <th style={cellHead}>ARICA 推測 種別</th>
              <th style={{ ...cellHead, textAlign: 'right' }}>判定</th>
            </tr>
          </thead>
          <tbody>
            {remaining.map((r) => (
              <tr key={r.rowid} style={{ borderBottom: '1px solid #ece9e6' }}>
                <td style={cellBody}>#{r.rowid}</td>
                <td style={cellBody}>{r.partner_name || '(未設定)'}</td>
                <td style={cellBody}>{r.receive_date || '—'}</td>
                <td style={cellBody}>
                  {r.arica_document_type ? (
                    <span style={{
                      fontSize: 11, padding: '1px 6px',
                      background: '#f5f3f1', color: '#8a8580',
                      borderRadius: 3, fontStyle: 'italic',
                    }}>{r.arica_document_type} ?</span>
                  ) : '—'}
                </td>
                <td style={{ ...cellBody, textAlign: 'right' }}>
                  <HStack gap={0.5} alignItems="center" justifyContent="end">
                    <Button
                      appearance="primary"
                      small
                      disabled={busyRowid === r.rowid}
                      onClick={() => apply(r.rowid, 'bank_auto_debit')}
                    >
                      これは引落
                    </Button>
                    <Button
                      appearance="secondary"
                      small
                      disabled={busyRowid === r.rowid}
                      onClick={() => apply(r.rowid, '25th_payment')}
                    >
                      これは 25日払い
                    </Button>
                  </HStack>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </ModalShell>
  );
};
